import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { MessageSquare, Send, User, Search, Check, CheckCheck, Lock, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Sidebar } from "./Dashboard";
import { useAuth, API } from "../App";
import { toast } from "sonner";

const Messages = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, getAuthHeaders } = useAuth();
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [membershipActive, setMembershipActive] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setConversations(response.data.conversations);
      setMembershipActive(response.data.membership_active);
    } catch (error) {
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchMessages = useCallback(async (partnerId) => {
    try {
      const response = await axios.get(`${API}/messages?with_user_id=${partnerId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setMessages(response.data.messages.reverse());
    } catch (error) {
      toast.error("Failed to load messages");
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    const toUserId = searchParams.get('to');
    if (toUserId) {
      const fetchUserAndStartConversation = async (userId) => {
        try {
          const response = await axios.get(`${API}/directory/${userId}`, {
            headers: getAuthHeaders(),
            withCredentials: true
          });
          setSelectedConversation({
            partner: {
              user_id: response.data.user_id,
              name: response.data.name,
              picture: response.data.picture
            },
            last_message: null,
            unread_count: 0
          });
        } catch (error) {
          toast.error("Failed to start conversation");
        }
      };
      fetchUserAndStartConversation(toUserId);
    }
  }, [searchParams, getAuthHeaders]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.partner.user_id);
    }
  }, [selectedConversation, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSendingMessage(true);
    try {
      await axios.post(`${API}/messages`, {
        to_user_id: selectedConversation.partner.user_id,
        content: newMessage
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      setNewMessage("");
      fetchMessages(selectedConversation.partner.user_id);
      fetchConversations();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === "message_limit_reached") {
        toast.error(`You've used all ${detail.limit} free messages. Upgrade to continue messaging.`);
      } else {
        toast.error("Failed to send message");
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      <Sidebar currentPath={location.pathname} />
      
      <main className="pt-20 lg:pt-8 p-4 lg:p-8 h-screen lg:h-auto" data-testid="messages-main">
        <div className="max-w-7xl mx-auto h-full lg:h-[calc(100vh-8rem)]">
          <div className="bg-white rounded-xl border border-slate-200 h-full flex overflow-hidden">
            {/* Conversations List */}
            <div className={`w-full md:w-80 border-r border-slate-200 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
              <div className="p-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 mb-3">Messages</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search conversations..." className="pl-9 h-9" />
                </div>
              </div>

              <ScrollArea className="flex-1">
                {loading ? (
                  <div className="p-4 space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3 animate-pulse">
                        <div className="w-12 h-12 bg-slate-200 rounded-full"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : conversations.length > 0 ? (
                  <div className="p-2">
                    {conversations.map((conv) => (
                      <button
                        key={conv.partner.user_id}
                        onClick={() => setSelectedConversation(conv)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                          selectedConversation?.partner.user_id === conv.partner.user_id
                            ? 'bg-blue-50'
                            : 'hover:bg-slate-50'
                        }`}
                        data-testid={`conversation-${conv.partner.user_id}`}
                      >
                        <div className="relative">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={conv.partner.picture} />
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {conv.partner.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          {conv.unread_count > 0 && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-900 truncate">{conv.partner.name}</p>
                            {conv.last_message && (
                              <span className="text-xs text-slate-500">
                                {formatDate(conv.last_message.created_at)}
                              </span>
                            )}
                          </div>
                          {conv.last_message && (
                            <p className="text-sm text-slate-500 truncate">
                              {conv.last_message.from_user_id === user?.user_id ? 'You: ' : ''}
                              {conv.last_message.content}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-600">No conversations yet</p>
                    <p className="text-sm text-slate-500">Start by messaging a physician from the directory</p>
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Chat Area */}
            {selectedConversation ? (
              <div className="flex-1 flex flex-col">
                {/* Chat Header */}
                <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                  <button 
                    className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
                    onClick={() => setSelectedConversation(null)}
                  >
                    ←
                  </button>
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={selectedConversation.partner.picture} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {selectedConversation.partner.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-slate-900">{selectedConversation.partner.name}</p>
                    <p className="text-sm text-slate-500">Physician</p>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((msg, index) => {
                      const isOwn = msg.from_user_id === user?.user_id;
                      const isLocked = msg.locked;
                      const showDate = index === 0 || 
                        formatDate(messages[index - 1]?.created_at) !== formatDate(msg.created_at);
                      
                      return (
                        <div key={msg.message_id}>
                          {showDate && (
                            <div className="text-center my-4">
                              <span className="text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                                {formatDate(msg.created_at)}
                              </span>
                            </div>
                          )}
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
                              {isLocked ? (
                                <div className="px-4 py-3 rounded-2xl bg-slate-100 rounded-bl-md border border-dashed border-slate-300">
                                  <div className="flex items-center gap-2 text-slate-500">
                                    <Lock className="w-4 h-4" />
                                    <span className="text-sm">Message from {selectedConversation?.partner.name?.split(' ')[0]}</span>
                                  </div>
                                  <p className="text-xs text-slate-400 mt-1">Upgrade to read replies</p>
                                </div>
                              ) : (
                                <div className={`px-4 py-2 rounded-2xl ${
                                  isOwn 
                                    ? 'bg-primary text-white rounded-br-md' 
                                    : 'bg-slate-100 text-slate-900 rounded-bl-md'
                                }`}>
                                  {msg.content}
                                </div>
                              )}
                              <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                                <span className="text-xs text-slate-500">{formatTime(msg.created_at)}</span>
                                {isOwn && !isLocked && (
                                  msg.read 
                                    ? <CheckCheck className="w-3 h-3 text-primary" />
                                    : <Check className="w-3 h-3 text-slate-400" />
                                )}
                              </div>
                            </div>
                          </motion.div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Message Input or Upgrade Prompt */}
                {!membershipActive && messages.some(m => m.locked) ? (
                  <div className="p-4 border-t border-slate-200 bg-amber-50">
                    <div className="flex items-center gap-3">
                      <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-900">You have unread replies</p>
                        <p className="text-xs text-amber-700">Upgrade to read messages and unlock full platform access</p>
                      </div>
                      <Button
                        size="sm"
                        className="bg-primary text-white hover:bg-primary/90 rounded-full text-xs"
                        onClick={() => window.location.href = "/#pricing"}
                        data-testid="upgrade-messages-btn"
                      >
                        <CreditCard className="w-3 h-3 mr-1" />
                        Upgrade
                      </Button>
                    </div>
                  </div>
                ) : null}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-200">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1"
                      disabled={sendingMessage}
                      data-testid="message-input"
                    />
                    <Button 
                      type="submit" 
                      className="bg-primary text-white hover:bg-primary/90"
                      disabled={!newMessage.trim() || sendingMessage}
                      data-testid="send-message-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="hidden md:flex flex-1 items-center justify-center text-center p-8">
                <div>
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Select a conversation</h3>
                  <p className="text-slate-600">Choose a conversation from the list or start a new one from the directory</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Messages;
